/* request.js */

import { cryptoWords } from '@/cryptowords';
import { client, states, token, user, filters } from '@/stores';
import { sleep } from '@/helpers/utils';
import { auth, updateFilters, updateBindings } from '@/helpers/auth';
import { push as notificationPush } from '@/helpers/notifications';
import { validate } from '@/helpers/validate';


const BINDINGS = [
	'chat-messages',
	'log-events',
	'collections-items',
	'requests',
	'minprices-requests',
	'guarantees-requests',
];


const CFG = {
	requestTimeout: 7000,
	requestRetryTimeout: 1000,
	APIRefreshInterval: 3600000,
	cryptoWords: cryptoWords,
	exceptionMessages: {
		'timeoutException': [ 504, 'Незавершённый запрос' ],
		'abortException': [ 500, 'Прерванный запрос' ],
		'errorException': [ 500, 'Непредвиденная ошибка' ],
		'parseException': [ 520, 'Ошибка обработки ответа' ],
		'responseException': [ 520, 'Неверный ответ сервера' ],
		'initializationException': [ 523, 'Сервер не инициализирован' ],
	},
};

const MODELS = {
	servers: {
		api: {
			required: true,
			type: 'string',
			mask: /^https?\:\/\/[0-9A-Za-z\.\_\-\:\/]{5,}$/,
		},
		wso: {
			required: true,
			type: 'string',
			mask: /^wss?\:\/\/[0-9A-Za-z\.\_\-\:\/]{5,}$/,
		},
	}
};


/* APIController */
const APIController = {
	req: {
		method: 'POST',
		url: states.pull('wlc') + '/acquire',
		body: client.pull(),
	},
	refresh: function (curTimeStamp, onRequest, onResponse) {
		return new Promise(
			(resolve, reject) => {
				const welcome = states.pull('welcome');
				if (welcome && welcome + CFG.APIRefreshInterval >= curTimeStamp) {
					resolve(null);
					return;
				}
				requestWrapper(this.req, onRequest, onResponse)
					.then(
						result => {
							if (result.status == 200)
								if (validate(result.data, MODELS.servers)) {
									states.push({
										welcome: curTimeStamp,
										api: result.data.api,
										wso: result.data.wso,
									});
									resolve(result);
									return;
								}
							reject(result);
						}
					)
					.catch(
						result => reject(result)
					);
			}
		)
		.then(
			result => result
		)
		.catch(
			result => result
		);
	},
};


/* request */
export async function request({
	path,
	data,
	cache = false,
	forceAPIRequest = false,
	onRequest = null,
	onResponse = null,
}) {
	states.push({
		loading: true,
	});
	const curTimeStamp = Date.now();
	let result;
	if (!forceAPIRequest) {
		result = await APIController.refresh(curTimeStamp, onRequest, onResponse);
		if (result !== null)
			sendNotifications(result.data, [ 'acquire', APIController.req ]);
	}
	if (states.isInitialized()) {
		result = await APIRequest(path, data, curTimeStamp, onRequest, onResponse);
		sendNotifications(result.data, [ 'request', result.request ]);
	}
	else if (forceAPIRequest) {
		result = {
			status: CFG.exceptionMessages['initializationException'][0],
			data: { _alert: CFG.exceptionMessages['initializationException'][1] },
			request: APIController.req,
		};
		sendNotifications(result.data, [ 'acquire', APIController.req ]);
	}
	states.push({
		loading: false,
	});
	return result;
}


/* monitor */
export async function monitor({
	key,
}) {
	const result = await requestWrapper({
		method: 'POST',
		url: states.pull('wlc') + '/monitor',
		body: { key: key },
	});
	return result;
}


/* sendNotifications */
function sendNotifications(data, source) {
	if (data._alert)
		notificationPush('alerts', data._alert, source);
	if (data._notification)
		notificationPush('notifications', data._notification, source);
}


/* APIRequest */
function APIRequest(path, data, curTimeStamp, onRequest, onResponse) {
	return new Promise(
		(resolve, reject) => {
			token.load();
			const req = {
				method: 'POST',
				url: states.pull('api') + path,
				body: Object.assign(
					data ? data : {},
					{
						_filters: Object.assign({}, filters.pullSelected(), { _update: filters.pull('_update') }),
						_rid: _ridEncode(user.pull('id'), curTimeStamp),
						_token: token.pull('token'),
					}
				),
			};
			requestWrapper(req, onRequest, onResponse)
				.then(
					result => {
						auth(result.data);
						updateFilters(result.data);
						updateBindings(result.data);
						resolve(result);
					}
				)
				.catch(
					result => reject(result)
				);
		}
	)
	.then(
		result => result
	)
	.catch(
		result => result
	);
}


/* requestWrapper */
function requestWrapper(req, onRequest, onResponse) {
	return new Promise(
		(resolve, reject) => {
			if (onRequest)
				onRequest(req);
			ajaxRequest(req)
				.catch(
					error => {
						if (onResponse)
							if (typeof error === 'string') {
								onResponse({
									status: CFG.exceptionMessages[error][0],
									data: { _alert: CFG.exceptionMessages[error][1] },
									request: req,
								});
							}
							else {
								onResponse(Object.assign({ request: req }, error));
							}
						return;
						// return sleep(CFG.requestRetryTimeout).then(
						// 	() => {
						// 		if (onRequest)
						// 			onRequest(req);
						// 		return ajaxRequest(req);
						// 	}
						// );
					}
				)
				.then(
					result => {
						result.request = req;
						if (onResponse)
							onResponse(result);
						if (result.status < 500) {
							resolve(result);
							return;
						}
						reject(result);
					}
				)
				.catch(
					error => {
						let result;
						if (typeof error === 'string') {
							result = {
								status: CFG.exceptionMessages[error][0],
								data: { _alert: CFG.exceptionMessages[error][1] },
								request: req,
							};
						}
						else {
							result = Object.assign({ request: req }, error);
						}
						if (onResponse)
							onResponse(result);
						reject(result);
					}
				);
		}
	);
}


/* ajaxRequest */
function ajaxRequest({ method = 'GET', url, body = null }) {
	return new Promise(
		(resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open(method, url);
			if ([ 'POST', 'PUT' ].indexOf(method) > -1)
				xhr.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
			xhr.timeout = CFG.requestTimeout;
			xhr.ontimeout = () => {
				reject('timeoutException');
			};
			xhr.onabort = () => {
				reject('abortException');
			};
			xhr.onerror = () => {
				reject('errorException');
			};
			xhr.onload = () => {
				let data;
				if (xhr.responseText) {
					try {
						data = JSON.parse(xhr.responseText);
					}
					catch (error) {
						reject('parseException');
						return;
					}
					data['_bindings'] = {};
					BINDINGS.forEach(
						header => {
							const result = xhr.getResponseHeader('x-binding-' + header);
							if (result)
								data['_bindings'][header.replace(/-/g, '_')] = parseInt(result);
						}
					);
					if (xhr.status < 500) {
						resolve({ status: xhr.status, data: data });
					}
					else {
						reject({ status: xhr.status, data: data });
					}
				}
				else {
					reject('responseException');
				}
			};
			xhr.send(body && typeof body === 'object' ? JSON.stringify(body) : body);
		}
	);
}


/* _ridEncode */
function _ridEncode(userId, epochTimeStamp) {
	const rstr = ('00' + Math.random().toString().substr(2, 3)).slice(-3);
	const ustr = ('000000000000000000' + userId.toString()).slice(-19);
	const level = parseInt(rstr.charAt(1));
	const offset = parseInt(rstr.charAt(2));
	const ulen = ustr.length;
	const sstr = ustr.substr(ulen - offset) + rstr + ustr.substr(0, ulen - offset);
	const slen = sstr.length;
	const cstr = CFG.cryptoWords[level];
	const clen = cstr.length;
	let result = rstr.substr(1);
	if (clen) {
		for (let i = 0; i < slen; i++)
			result += (parseInt(sstr.charAt(i)) ^ parseInt(cstr.charAt(i % clen))).toString(16);
	}
	else {
		result += sstr;
	}
	return epochTimeStamp.toString() + result;
}


/* _ridDecode */
function _ridDecode(rstr) {
	const level = parseInt(rstr.charAt(0));
	const offset = parseInt(rstr.charAt(1));
	const rlen = rstr.length;
	const cstr = CFG.cryptoWords[level];
	const clen = cstr.length;
	let result = '';
	if (clen) {
		for (let i = 15; i < rlen; i++)
			result += (parseInt(rstr.charAt(i), 16) ^ parseInt(cstr.charAt((i - 15) % clen))).toString();
	}
	else {
		result = rstr.substr(15);
	}
	result = result.substr(3 + offset) + result.substr(0, offset);
	return {
		userId: parseInt(result),
		epochTimeStamp: parseInt(rstr.substr(0, 13)),
	};
}
